import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

import config from '../../../../config.js';
import { getFile } from '../../../../util/file.js';

import { getContentBlockItemsByName } from '../../../Cms/CmsActions.js';
import { getDefaultLanguage, getTranslation } from '../../../Intl/IntlHelper.js';
import { getProductAttributeList, getProductPrice, getProductSlug, memoizedGetProducts } from '../../ProductActions.js';

import GenericSnippet from '../../../App/components/view/GenericSnippet.js';

// Returns the active products belonging to the category page (same matching logic as ProductsListWrapper).
export function getCategoryProducts(products, page, locale) {
	const categories = getContentBlockItemsByName((page || {}).blocks, 'category').map(cat => cat.text).filter(cat => cat).map(cat => `${cat}`.toLowerCase());
	if(!categories.length) {
		return [];
	}
	return products.filter(product => [
		...getProductAttributeList(product, 'categories', locale),
		...getProductAttributeList(product, 'categories', getDefaultLanguage()),
	].some(productCategory => categories.includes(`${productCategory}`.toLowerCase())));
}

function CategorySeo({ intl, page, title = '', description = '', cover = '', products = [] }) {
	const { locale } = intl;
	const categoryProducts = getCategoryProducts(products, page, locale);

	const firstProductImage = categoryProducts.length && (categoryProducts[0].pictures || [])[0]
		? getFile(categoryProducts[0].pictures[0])
		: '';

	// Cover if set, otherwise fall back to the first product image (GenericSnippet defaults to the logo when empty).
	const snippetImage = cover ? getFile(cover) : firstProductImage;

	const itemListLd = categoryProducts.length > 0 && JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: title,
		numberOfItems: categoryProducts.length,
		itemListElement: categoryProducts.map((product, index) => {
			const url = `${config.url}${locale}/${getProductSlug(product, locale)}`;
			const price = getProductPrice(product, null, null, true, null);
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@type': 'Product',
					name: getTranslation(product, 'name', locale),
					url,
					image: (product.pictures || [])[0] ? getFile(product.pictures[0]) : undefined,
					...(price > 0 ? {
						offers: {
							'@type': 'Offer',
							priceCurrency: 'EUR',
							price: Number(price).toFixed(2),
							availability: 'https://schema.org/InStock',
							url,
						},
					} : {}),
				},
			};
		}),
	});

	return (
		<Fragment>
			<GenericSnippet title={title} description={description} image={snippetImage} />

			{(itemListLd || (!cover && firstProductImage)) && (
				<Helmet
					meta={!cover && firstProductImage ? [
						{ property: 'og:image', content: firstProductImage },
						{ name: 'twitter:image', content: firstProductImage },
					] : []}
				>
					{itemListLd && <script type="application/ld+json">{itemListLd}</script>}
				</Helmet>
			)}
		</Fragment>
	);
}

function mapStateToProps(store) {
	return {
		products: memoizedGetProducts(store, { isActive: true, isPack: false, isVariation: false }),
	};
}

CategorySeo.propTypes = {
	intl: PropTypes.object.isRequired,
	page: PropTypes.object,
	title: PropTypes.string,
	description: PropTypes.string,
	cover: PropTypes.string,
	products: PropTypes.arrayOf(PropTypes.object),
};

export default connect(mapStateToProps)(injectIntl(CategorySeo));
